import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { useAppStore } from './appStore';
import { AuthUser } from '../types';
import { resetAllProductRelatedStores } from '../utils';
import { useArrivalReceiptStore } from './arrivalReceiptStore';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  stationId: string | null;

  setStationId: (stationId: string | null) => void;
  restoreSession: () => Promise<void>;
  setAuthenticatedUser: (user: AuthUser) => void;
  clearAuthState: () => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: 'loading',
      user: null,
      stationId: null,

      setStationId: (stationId) => set({ stationId }),

      setAuthenticatedUser: (user) =>
        set({
          user,
          status: 'authenticated',
        }),

      clearAuthState: () =>
        set({
          user: null,
          status: 'unauthenticated',
          stationId: null,
        }),

      restoreSession: async () => {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;

          if (!idToken) {
            set({
              user: null,
              status: 'unauthenticated',
            });
            return;
          }

          const payload = idToken.payload as Record<string, unknown>;
          const user: AuthUser = {
            id: (payload.name as string) || (payload.email as string) || '',
            email: (payload.email as string) || undefined,
            name: (payload.name as string) || (payload.email as string) || '',
            sub: (payload.sub as string) || '',
          };

          set({
            user,
            status: 'authenticated',
          });
        } catch {
          set({
            user: null,
            status: 'unauthenticated',
          });
        }
      },

      logout: async () => {
        try {
          await signOut();
        } catch {
          // ignore remote sign-out failure, still clear local state
        }

        set({
          user: null,
          status: 'unauthenticated',
          stationId: null,
        });

        resetAllProductRelatedStores();
        useAppStore.getState().reset();
        useArrivalReceiptStore.getState().setCurrentArrivalReceipt(null);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        stationId: state.stationId,
      }),
    }
  )
);
